'use client'
import { useEffect, useState, useContext, useCallback } from 'react'
import { addUrlParameter } from 'utils/shared'
import { TypographyV2 as Typography } from 'components/Typography/TypographyV2'
import classNames from 'classnames'
import { AchieveLink } from 'components/AchieveLink'
import { LayoutContext } from 'providers/LayoutProvider'
import { mainProductConfig } from 'constants/mainProductConfig'
import { Phone } from 'react-feather'
import { useFeatureFlags } from '@/providers/FeatureFlagProvider/FeatureFlagProvider'
import { FEATURE_TOGGLES } from 'constants/featureToggles'
import { DirectMailerLinkNoMui } from 'components/DirectMailerLinkNoMui'
import { removeUrlParams } from 'utils/shared'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { unslug } from 'utils/routes'
import { CtaCallButton } from 'components/CtaCallButton'
import { LinkButtonNoMui } from 'components/LinkButtonNoMui/LinkButtonNoMui'

// ── Color constants (replaces AchieveTheme token lookups) ───────────────────
const COLORS = {
  neutral0: '#FFFFFF',
}

// ── Custom Range Slider (replaces MUI Slider) ──────────────────────────────

function RangeSlider({
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  value,
  step,
  min,
  max,
  marks = [],
  onChange,
  className,
}) {
  const percentage = ((value - min) / (max - min)) * 100

  // The thumb is 20px wide. The native range input positions the thumb center
  // between 10px (at min) and calc(100% - 10px) (at max) so it never overflows
  // the track. We mirror that offset so the filled track always reaches the
  // thumb centre: start at 10px, span across (100% - 20px) proportionally,
  // then add back the first 10px.
  const THUMB_SIZE = 20 // px — must match the thumb width below
  const HALF_THUMB = THUMB_SIZE / 2

  const handleChange = useCallback(
    (e) => {
      onChange?.({ target: { value: Number(e.target.value) } })
    },
    [onChange]
  )

  return (
    <div className={classNames('relative w-full pt-2 pb-8', className)}>
      {/* Track background + filled track */}
      <div className="relative h-2.5 w-full rounded-full bg-[#E7ECF3] overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full rounded-l-full bg-[#94bbfe]"
          style={{
            width: `calc(${percentage}% + ${HALF_THUMB - (percentage / 100) * THUMB_SIZE}px)`,
          }}
        />
      </div>

      {/* Native range input (invisible but interactive) */}
      <input
        type="range"
        aria-label={ariaLabel}
        data-testid={dataTestId}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        className={classNames(
          'absolute top-2 left-0 w-full h-2.5 appearance-none bg-transparent cursor-pointer',
          // Webkit thumb
          '[&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:w-5',
          '[&::-webkit-slider-thumb]:h-5',
          '[&::-webkit-slider-thumb]:rounded-full',
          '[&::-webkit-slider-thumb]:bg-[#94bbfe]',
          '[&::-webkit-slider-thumb]:border-2',
          '[&::-webkit-slider-thumb]:border-solid',
          '[&::-webkit-slider-thumb]:border-[#f5f9ff]',
          '[&::-webkit-slider-thumb]:shadow-md',
          // Firefox thumb
          '[&::-moz-range-thumb]:w-5',
          '[&::-moz-range-thumb]:h-5',
          '[&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:bg-[#94bbfe]',
          '[&::-moz-range-thumb]:border-2',
          '[&::-moz-range-thumb]:border-solid',
          '[&::-moz-range-thumb]:border-[#f5f9ff]',
          '[&::-moz-range-thumb]:shadow-md',
          // Firefox track (hide default fill)
          '[&::-moz-range-track]:bg-transparent',
          '[&::-moz-range-track]:h-2.5'
        )}
      />

      {/* Mark labels */}
      {marks.length > 0 && (
        <div className="relative w-full mt-2">
          {marks.map((mark, idx) => {
            const pos = ((mark.value - min) / (max - min)) * 100
            return (
              <div
                key={idx}
                className="absolute"
                style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
              >
                {mark.label}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── SliderTool ──────────────────────────────────────────────────────────────

function SliderTool({
  ctaEvent,
  ctaText,
  ctaLinkBase,
  slider,
  sliderQueryParam,
  sectionVariationObject,
}) {
  const [sliderValue, setSliderValue] = useState(
    slider.defaultValue ? slider.defaultValue : slider.max / 2
  )
  const [ctaHref, setCtaHref] = useState('')
  const dollarUSLocale = Intl.NumberFormat('en-US')

  useEffect(() => {
    setCtaHref(addUrlParameter(ctaLinkBase, sliderQueryParam, sliderValue))
  }, [sliderValue, ctaLinkBase, sliderQueryParam])

  useEffect(() => {
    setSliderValue(slider.defaultValue ? slider.defaultValue : slider.max / 2)
  }, [slider])

  function handleChange(e) {
    setSliderValue(e.target.value)
  }

  return (
    <div
      className="shadow-none relative z-2 bg-transparent lg:relative lg:z-1 lg:w-full lg:left-0 lg:top-0"
      data-testid="product-hero-slider-container"
    >
      <div className="flex flex-wrap">
        <div className="w-full">
          <div
            className="w-full mt-0 mb-6 mx-0 lg:mt-0 lg:mb-6 lg:mx-0"
            data-testid="HomeHeroV2_slidertool"
          >
            <Typography
              content={`$${dollarUSLocale.format(sliderValue)}${
                sliderValue === slider.max && slider.maxPlus ? '+' : ''
              }`}
              variant="bodyLg"
              mobileVariant="bodyLg"
              responsive={true}
              fontWeight="medium"
              className="text-white text-center"
              data-testid="product-hero-dollar-value"
            />
            <RangeSlider
              aria-label="dollar-amount-selection-slider"
              data-testid="product-hero-slider-input"
              value={sliderValue}
              step={slider.step}
              min={slider.min}
              max={slider.max}
              marks={[
                {
                  value: slider.min,
                  label: (
                    <Typography
                      content={`$${dollarUSLocale.format(slider.min)}`}
                      variant="bodyLg"
                      fontWeight="medium"
                      className="text-white ml-[50px]"
                      data-testid="product-hero-slider-min"
                    />
                  ),
                },
                {
                  value: slider.max,
                  label: (
                    <Typography
                      content={`$${dollarUSLocale.format(slider.max)}${slider.maxPlus ? '+' : ''}`}
                      variant="bodyLg"
                      fontWeight="medium"
                      className="ml-[-80px] text-white"
                      data-testid="product-hero-slider-max"
                    />
                  ),
                },
              ]}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="w-full">
          <LinkButtonNoMui
            track={{
              ...{
                list_name: 'Home Hero CTA click',
                click_id: ctaText,
                click_text: ctaText,
                form_ss_amount: sliderValue,
                click_url: ctaHref,
                nav_link_section: 'Home Hero',
                track_event: 'internal_campaign_click',
                event_action: 'button_click',
                event_type: 'button_click',
              },
              ...ctaEvent,
              sectionVariationObject,
            }}
            content={{
              linkHref: ctaHref,
              linkText: ctaText,
            }}
            typographicOptions={{ variant: 'bodyLg', fontWeight: 'bold' }}
            variant="contained"
            color="secondary"
            className="min-w-max mt-0"
            fullWidth
          />
        </div>
      </div>
    </div>
  )
}

// ── PhoneCTA ────────────────────────────────────────────────────────────────

function PhoneCTA({ sectionVariationObject, event, phoneData }) {
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  return phoneData?.phoneNumber ? (
    <AchieveLink
      className="decoration-white"
      href={`tel:${phoneData.phoneNumber}`}
      track={{
        ...{
          list_name: 'Hero V2 Phone',
          click_id: phoneData.phoneNumber,
          click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${
            phoneData.phoneNumber
          }`,
          click_type: 'Phone Click',
          nav_link_section: 'Hero',
          track_event: 'button_click',
        },
        ...event,
        sectionVariationObject,
      }}
      underline="hover"
    >
      <div className="flex flex-row justify-center mt-4 md:justify-left">
        {/* Empty div to ignore !important css making phone image 345px tall */}
        <div></div>
        <Phone alt="phone button" width={24} height={24} color={COLORS.neutral0} />
        <Typography
          fontWeight="bold"
          variant="bodyLg"
          className="text-white ml-2 mr-0 my-0"
          content={phoneData.phoneNumber}
        />
      </div>
    </AchieveLink>
  ) : null
}

// ── DirectMailer ────────────────────────────────────────────────────────────

function DirectMailer({ sectionVariationObject, directMailerLink }) {
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const directMailerFlag = useFeatureFlags(FEATURE_TOGGLES[siteEnv].ACX_WEB_ENABLE_DIRECT_MAIL_LINK)
  const { fullAsPath } = useAppRouter()

  return directMailerFlag && directMailerLink ? (
    <DirectMailerLinkNoMui
      content={directMailerLink}
      track={{
        list_name: 'Home Hero Direct Mailer Click',
        nav_link_section: `${
          removeUrlParams(fullAsPath) === '/' ? 'Home' : unslug(fullAsPath).slice(1)
        } Hero`,
        click_id: directMailerLink?.linkText?.json?.content?.[0].content?.[0].value,
        click_text: directMailerLink?.linkText?.json?.content?.[0].content?.[0].value,
        click_url: directMailerLink?.linkHref,
        sectionVariationObject,
      }}
    />
  ) : null
}

// ── CLientCTA ───────────────────────────────────────────────────────────────

function CLientCTA({ phoneWrapper, button, sectionVariationObject, phoneData }) {
  const { fullAsPath } = useAppRouter()
  return (
    <CtaCallButton phoneWrapper={phoneWrapper} variant="white" phoneData={phoneData}>
      <LinkButtonNoMui
        track={{
          ...{
            list_name: 'Home Hero CTA click',
            nav_link_section: `${
              removeUrlParams(fullAsPath) === '/' ? 'Home' : unslug(fullAsPath).slice(1)
            } Hero`,
            click_id: button?.linkText?.json?.content?.[0]?.content[0]?.value,
            click_text: button?.linkText?.json?.content?.[0]?.content[0]?.value,
            click_url: button?.linkHref,
            track_event: 'internal_campaign_click',
            event_action: 'button_click',
            event_type: 'button_click',
          },
          ...button.event,
          sectionVariationObject,
        }}
        typographicOptions={{
          fontWeight: 'bold',
        }}
        variant="contained"
        color="secondary"
        content={button}
        className="w-full max-w-[280px] text-[#3300ff]"
        underline="hover"
      />
    </CtaCallButton>
  )
}

export { SliderTool, PhoneCTA, DirectMailer, CLientCTA }
